import {
  type AnyColumn,
  eq,
  type GetColumnData,
  isNull,
  or,
  type SQL,
  type Query,
  sql,
} from "drizzle-orm";

import { pgEnum, text, timestamp } from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";

export const LOCAL_DEV_USER_ID: string = "local-dev-user";
// Enum prefixing
export const leoEnum = pgEnum;

// Field definintion for ID's.
export const leoID = (columnName: string) => {
  return text(columnName)
    .primaryKey()
    .notNull()
    .$defaultFn(() => nanoid());
};

export type LeoIDKeyType = Extract<
  ReturnType<Parameters<ReturnType<typeof leoID>["$defaultFn"]>[0]>,
  ReturnType<typeof nanoid>
>;

// Field definintion for ID's that can be Null
export const leoForeignID = (columnName: string) => {
  return text(columnName);
};

// Field definintion for ID's that can not be Null
export const leoNotNullForeignID = (columnName: string) => {
  return leoForeignID(columnName).notNull();
};

// Converts SQL query into a string by replacing placeholders with actual parameter values.
export const getSqlStringFromQuery = (query: Query) => {
  let sqlString = query.sql;
  query.params.forEach((p, i) => {
    const regex = new RegExp(`\\$${i + 1}(?!\\d)`, "g");
    sqlString = sqlString.replace(regex, `'${p as string}'`);
  });

  return sqlString;
};

// Timestamp mixin to be appied to all tables.
export const timestampColumns = {
  createdAt: timestamp("createdAt", { mode: "date" }).defaultNow().notNull(),
};

// function to check if a text column is null or empty
export const isNullOrEmpty = (column: AnyColumn) =>
  or(isNull(column), eq(column, ""));

/**
@param columm - descrbes a column, which cast will be applied to
@param type - DB type, to which column will be casted to, should be one of DB supported types, @see https://www.postgresql.org/docs/current/datatype.html
*/
export const cast = <T>(column: AnyColumn, type: string) =>
  sql<T>`${column}::${sql.raw(type)}`;

export const aliasedColumn = <T extends AnyColumn>(
  column: T,
  alias: string,
): SQL.Aliased<GetColumnData<T>> =>
  column
    .getSQL()
    .mapWith((value) => column.mapFromDriverValue(value))
    .as(alias);

type SQLExpression<T = unknown> =
  | SQL<T>
  | SQL.Aliased<T>
  | AnyColumn<{ data: T }>;

/**
 * Builder class to create SQL CASE WHEN expression chain.
 */
export class SQLCaseWhen<T = never> {
  cases: SQL<T>;
  constructor(init?: SQL<T> | SQLCaseWhen<T>) {
    // Clone the initial cases to enable re-use.
    this.cases = init
      ? sql`${init instanceof SQLCaseWhen ? init.cases : init}`
      : sql<T>`CASE`;
  }

  /**
   * Add a case to the case expression.
   */
  when<Then>(whenExpr: SQLExpression, thenExpr: SQLExpression<Then>) {
    this.cases
      .append(sql` WHEN ${whenExpr} THEN ${thenExpr}`)
      .mapWith((v) => v as T | Then);
    return this as SQLCaseWhen<T | Then>;
  }

  /**
   * Add the else clause to the case expression.
   */
  else<Else>(elseExpr: SQLExpression<Else>) {
    return sql<T | Else>`${this.cases} ELSE ${elseExpr} END`.mapWith(
      (v) => v as T | Else,
    );
  }

  /**
   * Finish the case expression without an else clause, which will
   * return `null` if no case matches.
   */
  elseNull() {
    return sql<T | null>`${this.cases} END`.mapWith((v) => v as T | null);
  }
}

/**
 * Serves as a helper function to apply a SQL CASE WHEN expression, unfortunately DrizzleORM does not
 * support this natively yet.
 * Taken from:
 * @see {@link https://github.com/drizzle-team/drizzle-orm/issues/1065#issuecomment-3002014516}
 * @param whenExpr initial SQL expression for WHEN
 * @param thenExpr SQL expression for THEN
 * @returns SQL CASE WHEN plain sql expression
 */
export function caseWhen<Then>(
  whenExpr: SQLExpression,
  thenExpr: SQLExpression<Then>,
) {
  return new SQLCaseWhen().when(whenExpr, thenExpr);
}
